import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Dimensions,
  ScrollView,
  TouchableOpacity,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import FontFamily from '../../../../constants/fonts';

// Helper functions for responsive design
const isMobileView = () => {
  const windowWidth = Dimensions.get('window').width;
  return Platform.OS !== 'web' || windowWidth < 768;
};

const isIPadView = () => {
  return Platform.OS === 'ios' && Dimensions.get('window').width >= 768;
};

interface StepperNavigationProps {
  active: string;
  onChange: (id: string) => void;
  style?: any;
  favoriteSparcCards?: string[];
  favoriteLighthouseCards?: string[];
  favoriteLighthouseTasks?: string[];
  selectedKpiCards?: any[];
  isBackToEditInViewMode?: boolean;
  isViewMode?: boolean;
  isSubmitted?: boolean;
}

export const StepperNavigation: React.FC<StepperNavigationProps> = ({ 
  active, 
  onChange, 
  style,
  favoriteSparcCards = [],
  favoriteLighthouseCards = [],
  favoriteLighthouseTasks = [],
  selectedKpiCards = [],
  isBackToEditInViewMode = false,
  isViewMode = false,
  isSubmitted = false,
}) => {
  const steps = [
    { id: 'review-performance', title: 'Review Performance' },
    { id: 'key-activities', title: 'Key Activities' },
    { id: 'news', title: 'News' },
    { id: 'review-progress', title: 'Review Progress' },
  ];

  const activeIndex = steps.findIndex((s) => s.id === active);

  // Validation logic - allow navigation to current and previous steps
  const isStepEnabled = (stepId: string, stepIndex: number) => {
    // Disable all navigation when in view/submitted mode (when Back to Edit button is shown)
    if ((isViewMode || isSubmitted) && !isBackToEditInViewMode) return false;
    // Current and previous steps are always enabled (can navigate back)
    if (stepIndex <= activeIndex) return true;
    
    // Can't skip steps - must be on previous step to navigate to next
    if (stepIndex > activeIndex + 1) return false;
    
    // For next step, check if continue button would be enabled
    // Step 1 (Review Performance) -> Step 2 (Key Activities): Require selected KPIs to proceed
    if (stepIndex === 1 && activeIndex === 0) {
      return (selectedKpiCards && selectedKpiCards.length > 0);
    }
    
    if (stepIndex === 2 && activeIndex === 1) {
      return true;
    }
    
    // Step 3 (News) -> Step 4 (Review Progress): Always enabled
    if (stepIndex === 3 && activeIndex === 2) {
      return true;
    }
    
    return false;
  };

  // Handle step navigation with validation
  const handleStepChange = (stepId: string, stepIndex: number) => {
    if (isStepEnabled(stepId, stepIndex)) {
      onChange(stepId);
    }
  };

  // Responsive layout calculations
  const windowWidth = Dimensions.get('window').width;
  const mobile = isMobileView();
  const iPad = isIPadView();

  const tableCardHorizontalPadding = 24;
  const tableCardMaxWidth = 900;
  const baseSpacing = mobile ? 8 : 48; // Increased spacing between steps
  const paddingVertical = mobile ? 6 : iPad ? 28 : 8;
  const circleSize = mobile ? 14 : iPad ? 16 : 16;
  const progressBarHeight = 2;

  let stepWidth: number;
  let contentWidth: number | string;
  
  if (mobile) {
    const horizontalPadding = 16;
    const available = Math.max(windowWidth - horizontalPadding, 180);
    const calc = Math.floor((available - (steps.length - 1) * baseSpacing) / steps.length);
    stepWidth = Math.max(45, Math.min(calc, 56));
    contentWidth = steps.length * stepWidth + Math.max(0, steps.length - 1) * baseSpacing;
  } else if (iPad) {
    const available = Math.min(windowWidth - tableCardHorizontalPadding * 2, tableCardMaxWidth);
    stepWidth = 0;
    contentWidth = available;
  } else {
    stepWidth = 240; // Increased step width for more spacing
    contentWidth = steps.length * stepWidth + Math.max(0, steps.length - 1) * baseSpacing;
  }

  const stepSpacing = baseSpacing;
  let completedWidth: number;
  
  if (iPad) {
    const progressPercentage = Math.max(0, activeIndex) / Math.max(1, steps.length - 1);
    completedWidth = typeof contentWidth === 'number' ? contentWidth * progressPercentage : 0;
  } else {
    completedWidth = Math.max(0, activeIndex) * (stepWidth + stepSpacing);
  }

  return (
    <View style={[styles.stepperContainer, { alignItems: 'center', marginLeft: iPad ? 0 : mobile ? -8 : 0, marginRight: iPad ? 0 : 0, paddingHorizontal: iPad ? tableCardHorizontalPadding : mobile ? 8 : 0, marginBottom: iPad ? 32 : 0 }, style]}>
      {iPad ? (
        (() => {
          const baseAvailable = Math.min(windowWidth - tableCardHorizontalPadding * 2, tableCardMaxWidth);
          const segments = Math.max(1, steps.length - 1);
          const desiredExtraPerSegment = 40; // Increased for more gap between steps
          const extraTotal = desiredExtraPerSegment * Math.max(0, segments - 1);
          const maxAllowedWidth = Math.max(0, windowWidth - tableCardHorizontalPadding * 2);
          let widthToUse = Math.min(maxAllowedWidth, baseAvailable + extraTotal);
          widthToUse = Math.max(360, widthToUse);
          
          if (!widthToUse || steps.length < 2) return null;

          let effectiveCircleSize = circleSize;
          if (widthToUse < 520) effectiveCircleSize = Math.max(12, circleSize - 4);

          const totalTrackWidth = Math.max(0, widthToUse - effectiveCircleSize - 6);
          const segmentWidth = totalTrackWidth / segments;
          const bgLeft = effectiveCircleSize / 2 + 2;
          const bgWidth = totalTrackWidth;
          const completedW = Math.round(segmentWidth * Math.min(activeIndex, segments));
          const labelWidth = Math.min(140, Math.max(56, Math.floor(segmentWidth * 0.8)));

          const needsScroll = false;
          const extraStepperPaddingBottom = 20;

          const innerStepper = (
            <View style={{ width: widthToUse, position: 'relative', paddingVertical, paddingBottom: paddingVertical + extraStepperPaddingBottom, alignSelf: 'center' }}>
              {/* background track */}
              <View
                style={{
                  position: 'absolute',
                  left: bgLeft,
                  width: bgWidth,
                  height: progressBarHeight,
                  backgroundColor: '#4b4b4b',
                  top: paddingVertical + effectiveCircleSize / 2 - progressBarHeight / 2,
                  zIndex: 0,
                  borderRadius: progressBarHeight / 2,
                }}
              />

              {/* completed overlay */}
              {completedW > 0 && (
                <View
                  style={{
                    position: 'absolute',
                    left: bgLeft,
                    width: completedW,
                    height: progressBarHeight,
                    backgroundColor: '#005CBC',
                    top: paddingVertical + effectiveCircleSize / 2 - progressBarHeight / 2,
                    zIndex: 1,
                    borderRadius: progressBarHeight / 2,
                  }}
                />
              )}

              {/* circles */}
              {steps.map((step, idx) => {
                const isActive = idx === activeIndex;
                const isCompleted = idx < activeIndex;
                const isEnabled = isStepEnabled(step.id, idx);
                const centerX = bgLeft + idx * segmentWidth;
                return (
                  <TouchableOpacity
                    key={step.id}
                    style={{
                      position: 'absolute',
                      left: centerX - effectiveCircleSize / 2,
                      width: effectiveCircleSize,
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 2,
                      top: paddingVertical,
                      opacity: isEnabled ? 1 : 0.5,
                    }}
                    onPress={isEnabled ? () => handleStepChange(step.id, idx) : undefined}
                    activeOpacity={0.7}
                    disabled={!isEnabled}
                  >
                    <View
                      style={[
                        styles.stepCircle,
                        { width: effectiveCircleSize, height: effectiveCircleSize, borderRadius: effectiveCircleSize / 2 },
                        isCompleted
                          ? styles.stepCircleCompleted
                          : isActive
                            ? styles.stepCircleActive
                            : styles.stepCircleInactive,
                      ]}
                    >
                      {isCompleted && (
                        <MaterialIcons name="check" size={Math.max(8, Math.floor(effectiveCircleSize / 2))} color="#fff" />
                      )}
                    </View>
                  </TouchableOpacity>
                );
              })}

              {/* labels */}
              {steps.map((step, idx) => {
                const isActive = idx === activeIndex;
                const isEnabled = isStepEnabled(step.id, idx);
                const centerX = bgLeft + idx * segmentWidth;
                return (
                  <TouchableOpacity
                    key={`label-${step.id}`}
                    style={{
                      position: 'absolute',
                      left: centerX - labelWidth / 2,
                      width: labelWidth,
                      alignItems: 'center',
                      top: paddingVertical + effectiveCircleSize + 4,
                      zIndex: 2,
                      opacity: isEnabled ? 1 : 0.5,
                    }}
                    onPress={isEnabled ? () => handleStepChange(step.id, idx) : undefined}
                    activeOpacity={0.7}
                    disabled={!isEnabled}
                  >
                    <Text style={[styles.stepLabel, isActive ? styles.stepLabelActive : styles.stepLabelInactive]} numberOfLines={2}>
                      {step.title}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          );

          if (needsScroll) {
            return (
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ alignItems: 'flex-start' }}>
                {innerStepper}
              </ScrollView>
            );
          }

          return innerStepper;
        })()
      ) : (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ alignItems: 'center', paddingHorizontal: mobile ? 8 : 0 }}
        >
          <View style={{ width: contentWidth, position: 'relative', paddingVertical }}>
            {/* continuous background line */}
            <View
              style={{
                position: 'absolute',
                left: stepWidth / 2,
                width: typeof contentWidth === 'number' ? Math.max(0, contentWidth - stepWidth) : '100%',
                height: progressBarHeight,
                backgroundColor: '#4b4b4b',
                top: paddingVertical + circleSize / 2,
                zIndex: 0,
                borderRadius: 1,
              }}
            />

            {/* completed overlay */}
            {completedWidth > 0 && (
              <View
                style={{
                  position: 'absolute',
                  left: stepWidth / 2,
                  width: completedWidth,
                  height: progressBarHeight,
                  backgroundColor: '#005CBC',
                  top: paddingVertical + circleSize / 2,
                  zIndex: 1,
                  borderRadius: 1,
                }}
              />
            )}

            {/* circles */}
            <View style={{ flexDirection: 'row', alignItems: 'center', zIndex: 2 }}>
              {steps.map((step, idx) => {
                const isActive = idx === activeIndex;
                const isCompleted = idx < activeIndex;
                const isEnabled = isStepEnabled(step.id, idx);
                return (
                  <View key={step.id} style={{ width: stepWidth, alignItems: 'center', marginRight: idx === steps.length - 1 ? 0 : stepSpacing, opacity: isEnabled ? 1 : 0.5 }}>
                    <TouchableOpacity
                      style={{ alignItems: 'center', justifyContent: 'center' }}
                      onPress={isEnabled ? () => handleStepChange(step.id, idx) : undefined}
                      activeOpacity={0.7}
                      disabled={!isEnabled}
                    >
                      <View
                        style={[
                          styles.stepCircle,
                          { width: circleSize, height: circleSize, borderRadius: circleSize / 2 },
                          isCompleted
                            ? styles.stepCircleCompleted
                            : isActive
                              ? styles.stepCircleActive
                              : styles.stepCircleInactive,
                        ]}
                      >
                        {isCompleted && <MaterialIcons name="check" size={Math.max(8, Math.floor(circleSize / 2))} color="#fff" />}
                      </View>
                    </TouchableOpacity>
                  </View>
                );
              })}
            </View>
          </View>
        </ScrollView>
      )}

      {/* Labels row (visible on iPad and desktop, hidden on mobile) */}
      {!mobile && (
        iPad ? null : (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ alignItems: 'center', marginTop: 4 }}
          >
            <View style={{ width: contentWidth, flexDirection: 'row', alignItems: 'center' }}>
              {steps.map((step, idx) => {
                const isActive = idx === activeIndex;
                const isEnabled = isStepEnabled(step.id, idx);
                return (
                  <TouchableOpacity
                    key={step.id}
                    style={{ width: stepWidth, alignItems: 'center', marginRight: idx === steps.length - 1 ? 0 : stepSpacing, opacity: isEnabled ? 1 : 0.5 }}
                    onPress={isEnabled ? () => handleStepChange(step.id, idx) : undefined}
                    activeOpacity={0.7}
                    disabled={!isEnabled}
                  >
                    <Text style={[styles.stepLabel, isActive ? styles.stepLabelActive : styles.stepLabelInactive]} numberOfLines={2}>
                      {step.title}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </ScrollView>
        )
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  stepperContainer: {
    width: '100%',
    alignItems: 'center',
    // marginVertical: 12,
    marginLeft: Platform.OS === 'ios' ? 8 : 0,
    marginRight: Platform.OS === 'ios' ? 8 : 0,
    paddingHorizontal: Platform.OS === 'ios' ? 8 : 0,
  },
  stepCircle: {
    width: 16,
    height: 16,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 0,
    backgroundColor: '#fff',
  },
  stepCircleActive: {
    borderWidth: 4,
    borderColor: '#005CBC',
    backgroundColor: '#fff',
  },
  stepCircleCompleted: {
    borderWidth: 0,
    backgroundColor: '#005CBC',
  },
  stepCircleInactive: {
    borderWidth: 0,
    backgroundColor: '#4B4B4B',
  },
  stepLabel: {
    fontFamily: FontFamily.medium,
    fontSize: 14,
    textAlign: 'center',
    width: '100%',
  },
  stepLabelActive: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    color: '#005CBC',
    fontWeight: '700',
  },
  stepLabelInactive: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: '#6C757D',
    fontWeight: '400',
  },
});
